import axios from "axios";
import { baseURL } from "../../components/Data/baseURL";

/**
 * Main Axios instance used by the application.
 */
const axiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "X-CSRFToken": window.CSRF_TOKEN,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Separate instance used only for refreshing the access cookie.
 *
 * It has no response interceptor, preventing an infinite refresh loop
 * when the refresh endpoint itself returns 401.
 */
const refreshClient = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "X-CSRFToken": window.CSRF_TOKEN,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * All requests that fail with 401 while refresh is running share
 * the same promise.
 */
let refreshPromise = null;

const refreshAccessCookie = () => {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post("/api/jwt/refresh/")
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const isAuthenticationEndpoint = (url = "") => {
  return (
    url.includes("/api/jwt/create/") ||
    url.includes("/api/jwt/refresh/") ||
    url.includes("/api/logout/")
  );
};

axiosInstance.interceptors.request.use(
  (config) => {
    config.__serverRetryCount ??= 0;

    return config;
  },
  (error) => Promise.reject(error)
);

const retryableMethods = new Set([
  "get",
  "head",
  "options",
]);

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    if (!config) {
      return Promise.reject(error);
    }

    const method =
      config.method?.toLowerCase();

    const canRetryServerError =
      status >= 500 &&
      status < 600 &&
      retryableMethods.has(method);

    if (canRetryServerError) {
      if (
        config.__serverRetryCount >= 1
      ) {
        return Promise.reject(error);
      }

      config.__serverRetryCount += 1;

      return axiosInstance(config);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (
      isAuthenticationEndpoint(config.url)
    ) {
      return Promise.reject(error);
    }

    if (config.__authRetry) {
      return Promise.reject(error);
    }

    config.__authRetry = true;

    try {
      await refreshAccessCookie();

      return axiosInstance(config);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;